import { Meteor } from 'meteor/meteor';
import { Mongo } from 'meteor/mongo';
import { DeclareMethod } from '/imports/methods';
import { strings } from '/imports/strings';
import { validateBoolean, validateDate, validateNumber, validateString } from '/imports/validators';
import { Doc, GetMethodResponse, ListMethodArg, ListMethodResponse, ModelClass, References } from '/imports/types';
import { ConsumptionMeter } from '/imports/api/consumptionMeters';

export interface UtilityBill extends Doc {
  type: 'sdge' | 'pge' | 'sce';
  sourceId: string;
  meterId: string;
  _meter?: ConsumptionMeter;
  utilityAccountId: string;
  startDate: string;
  endDate: string;
  startTime: Date;
  endTime: Date;
  timezone?: string;
  amount: number;
  ccaAmount: number;
  electricityAmount: number;
  usage: number;
  production: number;
  consumption: number;
  consumptionFromGrid: number;
  productionConsumed: number;
  productionExported: number;
  tariffProfile?: string;
  pdf?: string;
  generationAmount?: number;
  demand?: number;
  usageRate?: number;
  distribRate?: number;
  demandRate?: number;
}
export type Model = UtilityBill;

export const UtilityBills = new Mongo.Collection<UtilityBill>('utilityBills');

type Refs = References<Model>

export const modelClass: ModelClass<Model, Refs> = {
  collection: UtilityBills,
  editableFields: ['active'],
  listMethod: DeclareMethod<ListMethodArg<UtilityBill>, ListMethodResponse<UtilityBill, Refs>>('listUtilityBills'),
  getMethod: DeclareMethod<string, GetMethodResponse<UtilityBill, Refs>>('getUtilityBill'),
  createMethod: DeclareMethod<Partial<UtilityBill>, string>('createUtilityBill'),
  updateMethod: DeclareMethod<Partial<UtilityBill>, string>('updateUtilityBill'),
  deleteMethod: DeclareMethod<string>('deleteUtilityBill'),
  pathPrefix: '/consumption-meters',
  asString: (document) => document.sourceId,
  modelName: strings.UtilityBill(),
  pluralName: strings.UtilityBills(),
  searchQuery: (search?: string) => {
    search = search?.trim();
    if (!search?.length) return {};
    return {
      $or: [
        { _id: search },
        { sourceId: new RegExp(search, 'gi') },
        { meterId: search as UtilityBill['type'] },
      ]
    };
  },
  validate: async (project: Partial<UtilityBill>) => {
    const errors: { [key: string]: string[] } = {};
    validateBoolean(errors, project, 'active');
    validateString(errors, project, 'type', { required: true });
    validateString(errors, project, 'sourceId', { required: true });
    validateString(errors, project, 'meterId', { required: true });
    validateString(errors, project, 'utilityAccountId', { required: true });
    validateString(errors, project, 'startDate', { required: true });
    validateString(errors, project, 'endDate', { required: true });
    validateDate(errors, project, 'startTime', { required: true });
    validateDate(errors, project, 'endTime', { required: true });
    validateNumber(errors, project, 'amount', { required: true });
    validateNumber(errors, project, 'ccaAmount', { required: true });
    validateNumber(errors, project, 'electricityAmount', { required: true });
    validateNumber(errors, project, 'usage', { required: true });
    validateNumber(errors, project, 'production', { required: true });
    validateNumber(errors, project, 'consumption', { required: true });
    validateNumber(errors, project, 'consumptionFromGrid', { required: true });
    validateNumber(errors, project, 'productionConsumed', { required: true });
    validateNumber(errors, project, 'productionExported', { required: true });
    return errors;
  },
};

Meteor.isServer && Meteor.startup(async () => {
  await UtilityBills.createIndexAsync({ meterId: 1 });
  await UtilityBills.createIndexAsync({ utilityAccountId: 1 });
  await UtilityBills.createIndexAsync({ startDate: 1 });
  await UtilityBills.createIndexAsync({ startTime: 1 });
  await UtilityBills.createIndexAsync({ type: 1 });
});